import { LoadingOutlined } from '@ant-design/icons';
import { Pagination, Skeleton, Spin } from 'antd';
import { useGetOrdersByTypeAndStatus } from 'lib/hooks/order/order.queries';
import { useESimsQuantity } from 'lib/hooks/sim/sim.queries';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import NumberFormat from 'react-number-format';
import { OrderStatus, OrderType } from 'types/shared';
import { ORDERS_COUNT_PER_PAGE } from 'views/SprDashboard/config';
import ApproveEsimOrderModal from '../ApproveEsimOrderModal/ApproveEsimOrderModal';
import ImpersonateConfirmationModal from '../ImpersonateConfirmationModal/ImpersonateConfirmationModal';
import {
  Order,
  RecentOrders,
} from '../RecentEsimOrdersList/RecentEsimOrdersList';
import RejectEsimOrderModal from '../RejectEsimOrderModal/RejectEsimOrderModal';

function PendingEsimOrdersList() {
  const [page, setPage] = useState(1);
  const [isImpersonateModalOpen, setIsImpersonateModalOpen] = useState(false);
  const [isApproveEsimOrderModalOpen, setIsApproveEsimOrderModalOpen] =
    useState(false);
  const [isRejectEsimOrderModalOpen, setIsRejectEsimOrderModalOpen] =
    useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);

  const { data: recentEsimOrdersData, isLoading: isEsimOrdersLoading } =
    useGetOrdersByTypeAndStatus({
      orderTypes: [OrderType.ESIM],
      orderStatuses: [OrderStatus.PENDING],
      page,
      limit: ORDERS_COUNT_PER_PAGE,
    });

  const {
    data: eSimsQuantityData,
    isLoading: isESimsQuantityLoading,
    isFetched: isESimsQuantityFetched,
  } = useESimsQuantity();

  const openImpersonateModal = (order: Order) => {
    setSelectedOrder(order);
    setIsImpersonateModalOpen(true);
  };

  const closeImpersonateModal = () => {
    setSelectedOrder(null);
    setIsImpersonateModalOpen(false);
  };

  const openApproveEsimOrderModal = (order: Order) => {
    setSelectedOrder(order);
    setIsApproveEsimOrderModalOpen(true);
    setIsRejectEsimOrderModalOpen(false);
  };

  const closeApproveEsimOrderModal = () => {
    setSelectedOrder(null);
    setIsApproveEsimOrderModalOpen(false);
  };

  const openRejectEsimOrderModal = (order: Order) => {
    setSelectedOrder(order);
    setIsRejectEsimOrderModalOpen(true);
    setIsApproveEsimOrderModalOpen(false);
  };

  const closeRejectEsimOrderModal = () => {
    setSelectedOrder(null);
    setIsRejectEsimOrderModalOpen(false);
  };

  const orders = recentEsimOrdersData?.orders ?? [];
  const noRecordsFound = !isEsimOrdersLoading && !orders.length;

  const eSimsQuantity = eSimsQuantityData?.quantity ?? 'N/A';

  const renderContent = () => {
    if (noRecordsFound) return <EmptyState />;

    return (
      <RecentEsimOrdersTable
        orders={orders}
        openImpersonateModal={openImpersonateModal}
        openApproveEsimOrderModal={openApproveEsimOrderModal}
        openRejectEsimOrderModal={openRejectEsimOrderModal}
      />
    );
  };

  return (
    <Spin
      spinning={isEsimOrdersLoading}
      size="large"
      indicator={
        <LoadingOutlined
          style={{ fontSize: 30, color: '#000' }}
          spin
          data-testid="loading-spinner"
        />
      }
    >
      <div className="card mb-3">
        <div className="card-header d-flex align-items-center justify-content-between">
          <div>
            <h5 className="card-title">eSim Orders Pending Approval</h5>
          </div>

          <div className="m-0">
            Unallocated eSIMs Available:{' '}
            {isESimsQuantityLoading && !isESimsQuantityFetched ? ( //  to show loading only on 1st fetch and keep the value displayed when re-fetching
              <Skeleton.Button active size="small" shape="round" />
            ) : (
              eSimsQuantity
            )}
          </div>
        </div>

        {renderContent()}
      </div>

      <div className="text-left">
        <Pagination
          className="mb-5"
          hideOnSinglePage
          onChange={(page) => setPage(page)}
          current={page}
          pageSize={ORDERS_COUNT_PER_PAGE}
          defaultCurrent={page}
          total={recentEsimOrdersData?.ordersCount}
          showSizeChanger={false}
        />
      </div>

      <ImpersonateConfirmationModal
        isOpen={isImpersonateModalOpen}
        onClose={closeImpersonateModal}
        selectedOrder={selectedOrder}
      />

      <ApproveEsimOrderModal
        isOpen={isApproveEsimOrderModalOpen}
        onClose={closeApproveEsimOrderModal}
        selectedOrder={selectedOrder}
      />

      <RejectEsimOrderModal
        isOpen={isRejectEsimOrderModalOpen}
        onClose={closeRejectEsimOrderModal}
        selectedOrder={selectedOrder}
      />
    </Spin>
  );
}

export default PendingEsimOrdersList;

function EmptyState() {
  return (
    <div className="blank__table__card" style={{ boxShadow: 'none' }}>
      <div className="blank__table__card__inner">No records found</div>
    </div>
  );
}

type RecentEsimOrdersTableProps = {
  orders: RecentOrders;
  openImpersonateModal: (order: Order) => void;
  openApproveEsimOrderModal: (order: Order) => void;
  openRejectEsimOrderModal: (order: Order) => void;
};

function RecentEsimOrdersTable({
  orders,
  openImpersonateModal,
  openApproveEsimOrderModal,
  openRejectEsimOrderModal,
}: RecentEsimOrdersTableProps) {
  const headers = [
    'Order Date',
    'Company',
    'Order Number',
    'Ordered By',
    'eSIMs Ordered',
    '',
    '',
  ] as const;

  return (
    <div className="table-responsive">
      <table className="table custom-table-secondary table-nowidth">
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={h + i}>
                <span className="table-column-title">{h}</span>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {orders.map((o) => {
            const {
              orderId,
              orderDate,
              orderedBy,
              companyName,
              orderNumber,
              orderedSims,
            } = o;

            const orderDateContent = orderDate
              ? moment(orderDate).format('MMM DD, YYYY h:mm a z')
              : '-';

            return (
              <tr key={orderId}>
                <td>{orderDateContent}</td>
                <td>{companyName}</td>
                <td>{orderNumber}</td>
                <td>
                  {' '}
                  <span
                    className="text-underline btn-link-orange"
                    onClick={() => openImpersonateModal(o)}
                  >
                    {orderedBy}
                  </span>
                </td>
                <td>
                  <NumberFormat
                    thousandSeparator={true}
                    displayType={'text'}
                    value={orderedSims}
                  />
                </td>

                <td>
                  <span
                    onClick={() => openApproveEsimOrderModal(o)}
                    className="text-underline text-green btn-link-orange"
                  >
                    Approve
                  </span>
                </td>
                <td>
                  <span
                    onClick={() => openRejectEsimOrderModal(o)}
                    className="text-underline btn-link-orange"
                  >
                    Reject
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
